import type { ComponentType } from "react";
import {
    FileText, FileSpreadsheet, FileImage, FileArchive, FileVideo, File as FileIcon, Folder, Presentation
} from "@fg/icons";
import { cn } from "@/lib/utils";

// Coloured file-kind tile shared by the reading-pane attachment tiles, the
// Drive picker and the Drive link cards — one mapping so a .xlsx looks the
// same everywhere in Mail.
const FILE_KINDS: { exts: string[]; icon: ComponentType<{ className?: string }>; tone: string }[] = [
    { exts: ["pdf"], icon: FileText, tone: "bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400" },
    { exts: ["doc", "docx", "rtf", "odt", "txt"], icon: FileText, tone: "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400" },
    { exts: ["xls", "xlsx", "csv", "ods"], icon: FileSpreadsheet, tone: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" },
    { exts: ["ppt", "pptx", "odp"], icon: Presentation, tone: "bg-orange-50 text-orange-600 dark:bg-orange-950/50 dark:text-orange-400" },
    { exts: ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "heic"], icon: FileImage, tone: "bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400" },
    { exts: ["zip", "rar", "7z", "gz", "tar"], icon: FileArchive, tone: "bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" },
    { exts: ["mp4", "mov", "avi", "mkv", "webm"], icon: FileVideo, tone: "bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-950/50 dark:text-fuchsia-400" }
];

export function FileTypeIcon({
    fileName,
    isFolder,
    className
}: {
    fileName: string;
    isFolder?: boolean;
    /** Size/shape override; defaults to the 36px attachment-tile square. */
    className?: string;
}) {
    if (isFolder) {
        return (
            <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-sky-50 text-sky-600 dark:bg-sky-950/50 dark:text-sky-400", className)}>
                <Folder className="h-5 w-5" />
            </span>
        );
    }
    const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
    const kind = FILE_KINDS.find((k) => k.exts.includes(ext));
    const Icon = kind?.icon ?? FileIcon;
    return (
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-md", kind?.tone ?? "bg-muted text-muted-foreground", className)}>
            <Icon className="h-5 w-5" />
        </span>
    );
}
